import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import ApiService from '@/services/api.service';
import PageTitle from '@/components/PageTitle';

type ResolutionLog = {
  id: string;
  dashboard_id: string;
  dashboard_name: string;
  resolved_at: string;
  triggered_by: string;
  logs: string[];
  superset_count: number;
  eligible_chart_count: number;
  excluded_chart_count: number;
  total_chart_count: number;
};

const formatRelative = (iso?: string | null): string => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function SupersetResolutionLogs() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery<ResolutionLog[]>({
    queryKey: ['superset-resolution-logs'],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/superset-resolution-logs',
      );
      return (result as { records: ResolutionLog[] }).records ?? (result as ResolutionLog[]);
    },
    staleTime: 15 * 1000,
  });

  const rows = data ?? [];

  return (
    <div className="p-8 h-full overflow-auto">
      <PageTitle title="Superset Resolution Logs" className="mb-6" />
      <p className="text-sm text-muted-foreground mb-4">
        Shows how each dashboard's charts were evaluated for superset optimization.
        Updated each time a dashboard is refreshed or the background reconciler runs.
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading resolution logs…
        </div>
      ) : isError ? (
        <div className="py-6 text-center text-sm text-destructive">
          Failed to load resolution logs.
        </div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-sm text-muted-foreground">
          No resolution logs yet. Logs appear after a dashboard with supersets enabled is refreshed.
        </div>
      ) : (
        <div className="border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Dashboard</TableHead>
                <TableHead>Resolved</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead className="text-center">Charts</TableHead>
                <TableHead className="text-center">Supersets</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const isExpanded = expandedId === row.id;
                return (
                  <>
                    <TableRow
                      key={row.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    >
                      <TableCell className="w-8 px-2">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.dashboard_name || row.dashboard_id}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span title={row.resolved_at}>
                          {formatRelative(row.resolved_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={row.triggered_by === 'reconcile' ? 'secondary' : 'outline'}>
                          {row.triggered_by}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        <span className="text-green-600">{row.eligible_chart_count}</span>
                        {' / '}
                        <span>{row.total_chart_count}</span>
                        {row.excluded_chart_count > 0 && (
                          <span className="text-muted-foreground text-xs ml-1">
                            ({row.excluded_chart_count} excluded)
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center text-sm font-medium">
                        {row.superset_count}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow key={`${row.id}-logs`}>
                        <TableCell colSpan={6} className="bg-muted/30 p-0">
                          <div className="px-6 py-4">
                            <h4 className="text-sm font-medium mb-2">Resolution Log</h4>
                            <div className="space-y-1 font-mono text-xs">
                              {row.logs.map((log, idx) => (
                                <div key={idx} className="py-0.5">{log}</div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
